import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DraftPickLog } from '@/types/draftPlayer';
import { DraftPlayer } from '@/types/draftPlayer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface RosterSlot {
  position: string;
  count: number;
}

interface DraftRosterProps {
  picks: DraftPickLog[];
  players: DraftPlayer[];
  teams: { id: string; name: string }[];
  myTeamId: string;
  // SAB-842: `slot_order` supplies an explicit, already-expanded slot list (e.g. NFL's
  // QB/RB1/RB2/.../BN6). When absent, MLB's roster_mins path below is unchanged.
  rosterConfig?: { roster_mins?: Record<string, number>; slot_order?: string[] };
  /**
   * SAB-842: playerId -> slot name, when the CALLER already knows the authoritative
   * assignment. Supplying this skips the two-pass derivation below entirely.
   *
   * NFL needs it. `nfl_acquire_pick_lock` assigns every drafted player a concrete slot
   * (QB, RB1, FLEX, IDP2, BN3...) at pick time and stores it in nfl_rosters.position_slot,
   * against the nfl_slot_eligibility table. Re-deriving that here would mean a second,
   * divergent copy of NFL's position->slot rules in the client -- the exact duplication
   * SAB-739 deleted ("do not reintroduce a literal here") -- and the naive string match
   * below would get it wrong anyway: FLEX matches no player's position, and every IDP
   * variant (SAF/OLB/NT/MLB/DE/DT...) would fall through to the bench.
   *
   * A roster view that disagrees with the roster is a correctness bug, not a cosmetic
   * one, so the server's answer wins. MLB passes nothing and is untouched.
   */
  slotAssignments?: Record<string, string>;
}

const positionColors: Record<string, string> = {
  C: 'text-amber-400',
  '1B': 'text-green-400',
  '2B': 'text-blue-400',
  '3B': 'text-red-400',
  SS: 'text-purple-400',
  OF: 'text-cyan-400',
  SP: 'text-orange-400',
  RP: 'text-pink-400',
  DH: 'text-yellow-400',
  UTIL: 'text-teal-400',
  BENCH: 'text-muted-foreground',
  P: 'text-orange-400',
};

// Default MLB roster slots based on typical config
const DEFAULT_SLOTS: RosterSlot[] = [
  { position: 'C', count: 1 },
  { position: '1B', count: 1 },
  { position: '2B', count: 1 },
  { position: '3B', count: 1 },
  { position: 'SS', count: 1 },
  { position: 'OF', count: 4 },
  { position: 'UTIL', count: 1 },
  { position: 'SP', count: 6 },
  { position: 'RP', count: 2 },
  { position: 'BENCH', count: 6 },
];

function buildSlots(rosterMins?: Record<string, number>): RosterSlot[] {
  if (!rosterMins) return DEFAULT_SLOTS;

  const order = ['C', '1B', '2B', '3B', 'SS', 'OF', 'DH', 'UTIL', 'SP', 'RP', 'P', 'BENCH'];
  const slots: RosterSlot[] = [];

  for (const pos of order) {
    const count = rosterMins[pos];
    if (count && count > 0) {
      slots.push({ position: pos, count });
    }
  }

  // Add any positions not in the order list
  for (const [pos, count] of Object.entries(rosterMins)) {
    if (!order.includes(pos) && count > 0) {
      slots.push({ position: pos, count });
    }
  }

  return slots.length > 0 ? slots : DEFAULT_SLOTS;
}

/** Normalize position for slot matching */
function normalizePos(pos: string): string {
  if (['LF', 'CF', 'RF'].includes(pos)) return 'OF';
  return pos;
}

const DraftRoster = ({ picks, players, teams, myTeamId, rosterConfig, slotAssignments }: DraftRosterProps) => {
  const [selectedTeamId, setSelectedTeamId] = useState(myTeamId);

  const selectedTeam = teams.find(t => t.id === selectedTeamId);
  // SAB-842: an explicit slot_order is already expanded (RB1, RB2 rather than {RB: 2}),
  // so each entry is its own count-1 row.
  const slots = useMemo(
    () => rosterConfig?.slot_order?.length
      ? rosterConfig.slot_order.map(position => ({ position, count: 1 }))
      : buildSlots(rosterConfig?.roster_mins),
    [rosterConfig],
  );

  // Get picks for the selected team, enriched with player data
  const teamPicks = useMemo(() => {
    return picks
      .filter(p => p.teamId === selectedTeamId)
      .map(p => {
        const player = players.find(pl => pl.id === p.playerId);
        return {
          ...p,
          playerName: player?.name || p.playerName || 'Unknown',
          position: player?.position || p.position || '?',
          teamAbbr: player?.team || '',
        };
      })
      .sort((a, b) => a.pickNumber - b.pickNumber);
  }, [picks, players, selectedTeamId]);

  // Assign picks to slots
  const filledSlots = useMemo(() => {
    const result: { position: string; pick: typeof teamPicks[0] | null }[] = [];
    const assigned = new Set<number>(); // indices of teamPicks already assigned

    // Expand slots into individual rows
    const expandedSlots: string[] = [];
    for (const slot of slots) {
      for (let i = 0; i < slot.count; i++) {
        expandedSlots.push(slot.position);
      }
    }

    // SAB-842: authoritative path. The caller already knows which slot each player
    // occupies, so place them directly and skip both derivation passes. Consumed once per
    // slot row so duplicate slot names (BN1..BN6 are distinct; two players never share
    // one) cannot double-render the same pick.
    if (slotAssignments) {
      const used = new Set<number>();
      return expandedSlots.map(slotPos => {
        const idx = teamPicks.findIndex(
          (p, i) => !used.has(i) && slotAssignments[p.playerId] === slotPos,
        );
        if (idx >= 0) {
          used.add(idx);
          return { position: slotPos, pick: teamPicks[idx] };
        }
        return { position: slotPos, pick: null };
      });
    }

    // First pass: assign players to their matching position slot
    for (const slotPos of expandedSlots) {
      let foundIdx = -1;
      for (let i = 0; i < teamPicks.length; i++) {
        if (assigned.has(i)) continue;
        const pickPos = normalizePos(teamPicks[i].position);
        if (slotPos === 'BENCH' || slotPos === 'UTIL' || slotPos === 'DH') continue; // handle flex/bench in second pass
        if (pickPos === slotPos || (slotPos === 'P' && (pickPos === 'SP' || pickPos === 'RP'))) {
          foundIdx = i;
          break;
        }
      }
      if (foundIdx >= 0) {
        assigned.add(foundIdx);
        result.push({ position: slotPos, pick: teamPicks[foundIdx] });
      } else {
        result.push({ position: slotPos, pick: null });
      }
    }

    // Second pass: fill UTIL/DH/BENCH with remaining unassigned picks
    const flexSlots = result.filter(r => (r.position === 'UTIL' || r.position === 'DH' || r.position === 'BENCH') && !r.pick);
    const remaining = teamPicks.filter((_, i) => !assigned.has(i));

    for (const pick of remaining) {
      const emptyFlex = flexSlots.find(f => !f.pick);
      if (emptyFlex) {
        emptyFlex.pick = pick;
      }
    }

    return result;
  }, [teamPicks, slots, slotAssignments]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          🏟️ Roster
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="ml-auto flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors max-w-[140px]">
                <span className="truncate">{selectedTeam?.name || 'Select Team'}</span>
                <ChevronDown className="w-3 h-3 shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-[300px] overflow-y-auto">
              {teams.map(t => (
                <DropdownMenuItem
                  key={t.id}
                  onClick={() => setSelectedTeamId(t.id)}
                  className={cn(t.id === selectedTeamId && 'bg-primary/10 text-primary')}
                >
                  <span className="truncate">{t.name}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="max-h-[250px] overflow-y-auto">
          <div className="space-y-0.5 p-3 pt-0">
            {filledSlots.map((slot, i) => (
              <div
                key={`${slot.position}-${i}`}
                className={cn(
                  "flex items-center gap-2 px-2 py-1.5 rounded-md text-sm",
                  slot.pick ? "bg-muted/20" : "bg-muted/10"
                )}
              >
                <span className={cn(
                  "font-mono text-xs w-10 shrink-0 font-semibold",
                  slot.pick ? positionColors[slot.position] || 'text-muted-foreground' : 'text-muted-foreground/50'
                )}>
                  {slot.position}
                </span>
                {slot.pick ? (
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    <span className="truncate text-foreground">{slot.pick.playerName}</span>
                    {slot.pick.teamAbbr && (
                      <span className="text-[10px] text-muted-foreground shrink-0">{slot.pick.teamAbbr}</span>
                    )}
                    <span className="ml-auto text-[10px] text-muted-foreground shrink-0">
                      R{slot.pick.round}.{slot.pick.pickInRound}
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground/40 italic">Empty</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DraftRoster;
